import * as Analytics from 'analytics/analytics'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { OptimizelyExperiments } from 'constants/optimizely'
import ModalContext from 'contexts/ModalContext'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import { rem } from 'polished'
import React, { memo, useCallback, useContext, useMemo } from 'react'
import getUSPContent from 'selectors/frontPage/getUSPContent'
import styled from 'styled-components'

const USPTile = styled(Group)`
  ${mediaQueryUp.tablet} {
    width: ${rem(296)};
  }

  ${mediaQueryUp.desktop} {
    width: unset;
  }
`

function FrontPageUSPComponent() {
  const uspItems = useAppSelector(getUSPContent)
  const showModal = useContext(ModalContext)

  const signUpModalTitleEnabled = useOptimizelyExperiment(OptimizelyExperiments.signUpModalTitleEnabled)
  const titleExperimentVariant = signUpModalTitleEnabled ? <>Exclusive prices and <i>more</i> with every booking</> : <>Huge savings and <i>more</i> with every booking</>
  const isAboutUsPageAvailable = useMemo<boolean>(() => !!getStaticPagesByType(['aboutUs']).length, [])

  const handleLearnMoreClick = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'learn_about_us',
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    })
  }, [])

  const handleSignupClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  return <VerticalSpacer gap={32}>
    <Heading variant="heading2">{titleExperimentVariant}</Heading>
    <CSSBreakpoint max="mobile">
      <Group gap={8} direction="vertical">
        {uspItems.map((item, index) => <BodyTextBlock
          key={index}
          variant="medium"
          colour="neutral-one"
          startIcon={<item.Icon />}
        >
          {item.body}
        </BodyTextBlock>)}
      </Group>
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <CardCarousel pageSize={uspItems.length}>
        {uspItems.map((item, index) => <USPTile key={index} direction="vertical" gap={12}>
          <item.Icon size="L" />
          <BodyText variant="medium">{item.body}</BodyText>
        </USPTile>)}
      </CardCarousel>
    </CSSBreakpoint>
    <Group
      gap={8}
      direction="vertical-reverse"
      tabletDirection="horizontal"
      tabletHorizontalAlign="end"
    >
      {isAboutUsPageAvailable && <TextButton kind="tertiary" onClick={handleLearnMoreClick} to="/about-us" target="_blank">
        Learn more about us
      </TextButton>}
      <TextButton kind="primary" onClick={handleSignupClick}>
        Sign up and save
      </TextButton>
    </Group>
  </VerticalSpacer>
}

export default memo(FrontPageUSPComponent)
